'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ThemeProvider } from '@/components/ThemeProvider';
import FloatingSidebar from '@/components/FloatingSidebar';
import HomeNotdienstNotice from '@/components/HomeNotdienstNotice';
import { toast } from '@/hooks/use-toast';

interface LayoutClientWrapperProps {
  children: React.ReactNode;
}

export default function LayoutClientWrapper({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    // CRITICAL: Scroll to top IMMEDIATELY on mount, before anything else
    // This must happen before React renders to prevent any scroll position
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    setMounted(true);
    
    // Disable browser scroll restoration to prevent automatic scrolling
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Also scroll immediately after mount
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);
  }, []);

  // Handle clicks on links to homepage - ensure they start at top
  useEffect(() => {
    if (!mounted) return;

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Find the closest link element (works with both regular <a> and Next.js Link)
      const link = target.closest('a');
      
      if (link) {
        const href = link.getAttribute('href');
        // Check if it's a link to homepage (with or without hash)
        // This includes: "/", "/#", "/#services", etc.
        if (href === '/' || (href && href.startsWith('/') && (href === '/#' || href.startsWith('/#')))) {
          // Force scroll to top before navigation
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
          
          // Store flag to ensure we stay at top after navigation
          // The hash navigation handler will then scroll to the hash if needed
          sessionStorage.setItem('scrollToTop', 'true');
          
          // ALWAYS set flag to indicate user navigated (not a reload)
          // This prevents the Notdienst-Hinweis from showing when navigating back to homepage
          // Even if we're already on homepage, if we click a link, it's navigation, not reload
          sessionStorage.setItem('navigatedToHomepage', 'true');
        }
      }
    };

    document.addEventListener('click', handleLinkClick, true);
    
    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [mounted]);

  // Set navigation flag when pathname changes (if navigating to homepage)
  useEffect(() => {
    if (!mounted) return;
    
    // If we're navigating TO the homepage from another page, set the flag
    // This prevents the banner from showing on navigation
    if (pathname === '/') {
      // Check if we came from another page (not initial load or reload)
      const previousPath = sessionStorage.getItem('previousPathname');
      if (previousPath && previousPath !== '/') {
        // We navigated from another page to homepage
        sessionStorage.setItem('navigatedToHomepage', 'true');
      }
      // Store current pathname for next navigation
      sessionStorage.setItem('previousPathname', pathname);
    } else {
      // Store current pathname
      sessionStorage.setItem('previousPathname', pathname);
    }
  }, [pathname, mounted]);

  // Scroll to top immediately when route changes (without animation)
  useEffect(() => {
    if (!mounted) return;
    
    // CRITICAL: Force scroll to top on every route change
    // This must happen BEFORE Next.js can restore scroll position
    const forceScrollToTop = () => {
      // Direct assignment is fastest - no animation
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
        document.documentElement.scrollLeft = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
        document.body.scrollLeft = 0;
      }
      window.scrollTo(0, 0);
    };
    
    // Execute immediately - multiple times to override everything
    forceScrollToTop();
    forceScrollToTop(); // Execute twice immediately
    
    // Execute in next ticks to catch any delayed scroll restoration
    const timeout1 = setTimeout(forceScrollToTop, 0);
    const timeout2 = setTimeout(forceScrollToTop, 1);
    const timeout3 = setTimeout(forceScrollToTop, 2);
    const timeout4 = setTimeout(forceScrollToTop, 5);
    const timeout5 = setTimeout(forceScrollToTop, 10);
    const timeout6 = setTimeout(forceScrollToTop, 20);
    const timeout7 = setTimeout(forceScrollToTop, 50);
    const timeout8 = setTimeout(forceScrollToTop, 100);
    const timeout9 = setTimeout(forceScrollToTop, 200);
    const timeout10 = setTimeout(forceScrollToTop, 300);
    
    // Use requestAnimationFrame as additional safeguard
    const raf1 = requestAnimationFrame(() => {
      forceScrollToTop();
      const raf2 = requestAnimationFrame(() => {
        forceScrollToTop();
        const raf3 = requestAnimationFrame(() => {
          forceScrollToTop();
        });
        return () => cancelAnimationFrame(raf3);
      });
      return () => cancelAnimationFrame(raf2);
    });
    
    // MutationObserver to continuously reset scroll position during page load
    // BUT: Stop immediately if user manually scrolls
    let observer: MutationObserver | null = null;
    let scrollCheckInterval: NodeJS.Timeout | null = null;
    let userHasScrolled = false;
    
    // Detect manual user scrolling
    const detectUserScroll = () => {
      userHasScrolled = true;
      // Stop all auto-scroll prevention immediately when user scrolls
      if (scrollCheckInterval) {
        clearInterval(scrollCheckInterval);
        scrollCheckInterval = null;
      }
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };
    
    if (typeof window !== 'undefined' && window.MutationObserver) {
      // Listen for user scroll events (wheel, touch, keyboard)
      window.addEventListener('wheel', detectUserScroll, { passive: true, once: true });
      window.addEventListener('touchstart', detectUserScroll, { passive: true, once: true });
      window.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'PageDown' || e.key === 'PageUp' || e.key === 'Home' || e.key === 'End') {
          detectUserScroll();
        }
      }, { once: true });
      
      observer = new MutationObserver(() => {
        // Only reset if user hasn't scrolled manually
        if (!userHasScrolled && (window.scrollY > 0 || document.documentElement.scrollTop > 0 || document.body.scrollTop > 0)) {
          forceScrollToTop();
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
      });
      
      // Also check scroll position continuously for the first 1 second ONLY
      // Stop immediately if user scrolls manually
      scrollCheckInterval = setInterval(() => {
        if (!userHasScrolled) {
          forceScrollToTop();
        } else {
          // User has scrolled, stop checking
          if (scrollCheckInterval) {
            clearInterval(scrollCheckInterval);
            scrollCheckInterval = null;
          }
        }
      }, 10); // Check every 10ms
      
      // Stop checking after 1 second maximum (or when user scrolls)
      setTimeout(() => {
        if (scrollCheckInterval) {
          clearInterval(scrollCheckInterval);
          scrollCheckInterval = null;
        }
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      }, 1000); // Reduced to 1 second
    }
    
    // Handle hash navigation ONLY if hash exists (for anchor links like /#services)
    const handleHashNavigation = () => {
      const hash = window.location.hash;
      if (hash && hash.length > 1) {
        const hashId = hash.substring(1);
        const element = document.getElementById(hashId);
        if (element) {
          // Wait for page to fully render before scrolling to hash
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 800);
        }
      }
    };

    // Only handle hash navigation if there's actually a hash
    if (window.location.hash) {
      setTimeout(handleHashNavigation, 800);
    }
    
    // NO scroll prevention - we only ensure page starts at top
    // This allows manual scrolling immediately while still preventing auto-scroll restoration
    // The multiple forceScrollToTop calls above are sufficient to keep page at top initially
    
    // Cleanup
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(timeout4);
      clearTimeout(timeout5);
      clearTimeout(timeout6);
      clearTimeout(timeout7);
      clearTimeout(timeout8);
      clearTimeout(timeout9);
      clearTimeout(timeout10);
      cancelAnimationFrame(raf1);
      if (observer) {
        observer.disconnect();
      }
      if (scrollCheckInterval) {
        clearInterval(scrollCheckInterval);
      }
    };
  }, [pathname, mounted]);

  useEffect(() => {
    if (!mounted) return;

    // Global keyboard shortcut for admin panel
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'S') {
        event.preventDefault();
        window.location.href = '/admin';
        toast({
          title: "Admin Dashboard",
          description: "Strg+Shift+S wurde gedrückt - Admin-Panel wird geöffnet",
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup keyboard event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mounted]);


  return (
    <ThemeProvider
      defaultTheme="light"
      storageKey="dental-theme"
    >
      {children}
      <FloatingSidebar />
      <HomeNotdienstNotice />
    </ThemeProvider>
  );
}
